import type { Command } from 'commander';
import chalk from 'chalk';
import { getSDKClient } from '../../lib/sdk-client.js';
import { OutputFormatter, getJsonFlag } from '../../lib/output.js';
import { MutagentError, handleError } from '../../lib/errors.js';
import {
  providerLinks,
  providerLink,
  renderPlainCard,
  echoDirectiveToStderr,
} from '../../lib/ui-links.js';

interface UpdateOptions {
  name?: string;
  apiKey?: string;
  active?: string;
  setDefault?: boolean;
  baseUrl?: string;
}

export function registerUpdateCommand(parent: Command): void {
  parent
    .command('update')
    .description('Update an existing provider configuration')
    .argument('<id>', 'Provider ID (from: mutagent providers list)')
    .option('-n, --name <name>', 'Updated display name')
    .option('-k, --api-key <key>', 'Updated API key (will be re-encrypted)')
    .option('--active <bool>', 'Activate or deactivate (true|false)')
    .option('--set-default', 'Set as default provider for its scope')
    .option('--base-url <url>', 'Updated base URL (use "" to clear)')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent providers update <id> --name "New Name"
  ${chalk.dim('$')} mutagent providers update <id> --api-key $NEW_KEY --json
  ${chalk.dim('$')} mutagent providers update <id> --active false
  ${chalk.dim('$')} mutagent providers update <id> --set-default --json

PATCH semantics — only provided fields are updated.
    `)
    .action(async (id: string, options: UpdateOptions) => {
      const isJson = getJsonFlag(parent);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        // Build update payload — only include provided fields
        const updateData: {
          name?: string;
          apiKey?: string;
          isActive?: boolean;
          isDefault?: boolean;
          baseUrl?: string | null;
        } = {};

        let hasUpdates = false;

        if (options.name !== undefined) {
          updateData.name = options.name;
          hasUpdates = true;
        }
        if (options.apiKey !== undefined) {
          updateData.apiKey = options.apiKey;
          hasUpdates = true;
        }
        if (options.active !== undefined) {
          if (options.active !== 'true' && options.active !== 'false') {
            throw new MutagentError(
              'INVALID_OPTION',
              `Invalid --active value: ${options.active}`,
              'Use --active true or --active false'
            );
          }
          updateData.isActive = options.active === 'true';
          hasUpdates = true;
        }
        if (options.setDefault) {
          updateData.isDefault = true;
          hasUpdates = true;
        }
        if (options.baseUrl !== undefined) {
          updateData.baseUrl = options.baseUrl === '' ? null : options.baseUrl;
          hasUpdates = true;
        }

        if (!hasUpdates) {
          throw new MutagentError(
            'NO_UPDATES',
            'No update fields provided',
            'Provide at least one of: --name, --api-key, --active, --set-default, --base-url'
          );
        }

        const client = await getSDKClient();
        const updated = await client.updateProvider(id, updateData);

        if (isJson) {
          const directive = buildProviderUpdatedDirective(updated, id);
          echoDirectiveToStderr(directive);
          output.output({
            success: true,
            ...updated,
            _links: providerLinks(updated.id ?? id),
            _directive: directive,
          });
        } else {
          output.success(`Provider updated: ${String(updated.name ?? id)}`);
          console.log(`  ID:  ${String(updated.id ?? id)}`);
          console.log(`  URL: ${providerLink(updated.id ?? id)}`);
          if (options.apiKey) {
            console.log(chalk.dim('  API key re-encrypted server-side.'));
          }
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });
}

function buildProviderUpdatedDirective(
  provider: { id?: number | string; name?: string },
  requestId: string,
) {
  const id = provider.id ?? requestId;
  const title = `Provider Updated — ${provider.name ?? String(id)}`;
  const dashboardUrl = providerLink(id);
  const apiUrl = `/api/providers/${String(id)}`;
  const rows = [
    { label: 'Provider ID', value: String(id) },
    ...(provider.name ? [{ label: 'Name', value: provider.name }] : []),
  ];
  const links = [
    { label: 'Settings', url: dashboardUrl },
    { label: 'API', url: apiUrl },
  ];
  const next = [
    `mutagent providers get ${String(id)} --json`,
    `mutagent providers test ${String(id)} --json`,
  ];
  return {
    display: 'status_card' as const,
    template: 'provider_updated',
    title,
    fields: { providerId: String(id), name: provider.name },
    links: { settings: dashboardUrl, api: apiUrl },
    next,
    instruction: 'MANDATORY: Display this card to the user before proceeding.',
    renderedCard: renderPlainCard({ title, icon: '~', rows, links, next }),
  };
}
